'use client'

import Link from 'next/link'
import Image from 'next/image'
import { useRef } from 'react'
import { useInView, useScrollProgress } from '@/hooks/useInView'
import {
  LayoutDashboard,
  MessageSquare,
  CalendarDays,
  FileText,
  Bot,
  ArrowRight,
  Check,
  BarChart3,
  Clock,
  Users,
  Target,
  Send,
  Zap,
  Bell,
  Repeat,
  Brain,
  Headphones,
  Sparkles,
  type LucideIcon,
} from 'lucide-react'
import { BOOK_A_DEMO_URL } from '@/lib/constants'
import { AttributedCtaLink } from '@/components/conversion/AttributedCtaLink'
import { RelatedGuides } from '@/components/seo/RelatedGuides'
import { FEATURES_LINKS } from '@/lib/internal-links'

/* ── Types ──────────────────────────────────────────────── */
interface FeatureBullet {
  text: string
  icon: LucideIcon
}

interface Feature {
  title: string
  subtitle: string
  description: string
  icon: LucideIcon
  accentWord: string
  bullets: FeatureBullet[]
  image: string
  imageAlt: string
}

/* ── Feature Data (all original copy preserved) ─────────── */
const features: Feature[] = [
  {
    title: 'Smart Dashboard',
    subtitle: 'Your command center',
    accentWord: 'Smart',
    description:
      'See everything at a glance — pipeline value, scheduled jobs, revenue, and crew performance in one real-time dashboard.',
    icon: LayoutDashboard,
    bullets: [
      { text: 'Real-time pipeline overview with stage tracking', icon: BarChart3 },
      { text: 'Revenue forecasting and job profitability', icon: Target },
      { text: 'Crew utilization and scheduling insights', icon: Users },
      { text: 'Weather-aware job planning with NOAA data', icon: Clock },
      { text: 'Custom KPI widgets and drill-down reports', icon: LayoutDashboard },
    ],
    image: '/assets/images/software-shots/smart-dashboard.png',
    imageAlt: 'BuilderLync smart dashboard showing total job value, jobs created, opportunities, and pipeline metrics',
  },
  {
    title: 'Instant Follow-Ups',
    subtitle: 'AI-powered lead nurture',
    accentWord: 'Instant',
    description:
      'Never lose a lead again. Sierra AI responds to new inquiries in under 60 seconds — via text, email, or voice — 24/7.',
    icon: MessageSquare,
    bullets: [
      { text: 'Sub-60-second response time on every lead', icon: Zap },
      { text: 'Multi-channel outreach: SMS, email, voicemail', icon: Send },
      { text: 'AI-crafted personalized messages per lead source', icon: Brain },
      { text: 'Automated drip sequences for long-term nurture', icon: Repeat },
      { text: 'Real-time notifications when leads engage', icon: Bell },
    ],
    image: '/assets/images/software-shots/instant-follow-ups.png',
    imageAlt: 'BuilderLync automation workflow list showing AI-powered follow-up sequences',
  },
  {
    title: 'Simple Scheduling',
    subtitle: 'Drag, drop, dispatch',
    accentWord: 'Simple',
    description:
      'Schedule inspections, crew jobs, and follow-ups with a visual calendar. Sync with Google Calendar automatically.',
    icon: CalendarDays,
    bullets: [
      { text: 'Drag-and-drop calendar for jobs and inspections', icon: CalendarDays },
      { text: 'Google Calendar two-way sync', icon: Repeat },
      { text: 'Automated appointment reminders via SMS', icon: Bell },
      { text: 'Crew assignment and availability tracking', icon: Users },
      { text: 'Route optimization for field teams', icon: Target },
    ],
    image: '/assets/images/software-shots/scheduling.png',
    imageAlt: 'BuilderLync calendar view with new event creation for inspections',
  },
  {
    title: 'One-Click Proposals',
    subtitle: 'Close deals faster',
    accentWord: 'One-Click',
    description:
      'Generate beautiful, branded proposals from satellite measurements in minutes. E-sign and collect deposits — all in one flow.',
    icon: FileText,
    bullets: [
      { text: 'EagleView-powered satellite measurements', icon: Target },
      { text: 'Auto-generated material lists and pricing', icon: FileText },
      { text: 'Built-in e-signature and deposit collection', icon: Check },
      { text: 'Good/Better/Best pricing options', icon: BarChart3 },
      { text: 'Branded PDF export and customer portal', icon: Send },
    ],
    image: '/assets/images/software-shots/proposals.png',
    imageAlt: 'BuilderLync proposals and invoices module with AI-powered proposal generation',
  },
  {
    title: 'Sierra AI Assistant',
    subtitle: 'Your 24/7 team member',
    accentWord: 'Sierra AI',
    description:
      'Sierra handles calls, books appointments, qualifies leads, and answers homeowner questions — around the clock.',
    icon: Bot,
    bullets: [
      { text: 'Voice AI that answers calls like a real person', icon: Headphones },
      { text: 'Qualifies leads and books appointments automatically', icon: CalendarDays },
      { text: 'Handles FAQs: pricing, availability, services', icon: MessageSquare },
      { text: 'Learns your business and adapts over time', icon: Brain },
      { text: 'Seamless handoff to your team when needed', icon: Users },
    ],
    image: '/assets/images/software-shots/sierra-ai.png',
    imageAlt: 'BuilderLync Sierra AI assistant overview showing agent configuration and system prompt',
  },
]

/* ── Parallax Section Wrapper ───────────────────────────── */
function ParallaxSection({
  children,
  className = '',
}: {
  children: React.ReactNode
  className?: string
}) {
  const ref = useRef<HTMLElement>(null)
  const progress = useScrollProgress(ref)
  // Map progress 0→1 to y offset 60→-60
  const y = 60 - progress * 120

  return (
    <section ref={ref} className={`section relative overflow-hidden ${className}`}>
      <div style={{ transform: `translateY(${y}px)` }} className="section-inner">
        {children}
      </div>
    </section>
  )
}

/* ── Screenshot Card with browser chrome ────────────────── */
function ScreenshotCard({
  feature,
  isInView,
}: {
  feature: Feature
  isInView: boolean
}) {
  return (
    <div
      className={`relative anim-scale-in anim-delay-2 ${isInView ? 'in-view' : ''}`}
    >
      {/* Ambient glow */}
      <div className="absolute -inset-6 bg-gradient-to-t from-red-600/8 via-red-600/4 to-transparent rounded-3xl blur-2xl pointer-events-none" />

      <div className="relative bg-zinc-950 border border-zinc-800/60 rounded-2xl overflow-hidden shadow-2xl shadow-black/50">
        {/* Browser Chrome */}
        <div className="flex items-center gap-2 px-4 py-2.5 bg-zinc-950/90 border-b border-zinc-800/50">
          <div className="flex gap-1.5">
            <div className="w-2.5 h-2.5 rounded-full bg-red-500/70" />
            <div className="w-2.5 h-2.5 rounded-full bg-yellow-500/70" />
            <div className="w-2.5 h-2.5 rounded-full bg-green-500/70" />
          </div>
          <div className="flex-1 mx-3">
            <div className="w-40 h-4 bg-zinc-800 rounded-md mx-auto flex items-center justify-center">
              <span className="text-[9px] text-zinc-600 hidden sm:inline">
                app.builderlync.com
              </span>
            </div>
          </div>
        </div>

        {/* Screenshot */}
        <Image
          src={feature.image}
          alt={feature.imageAlt}
          width={1200}
          height={700}
          className="w-full h-auto"
          sizes="(max-width: 768px) 100vw, 640px"
        />
      </div>
    </div>
  )
}

/* ── Feature Section Block ──────────────────────────────── */
function FeatureBlock({
  feature,
  index,
}: {
  feature: Feature
  index: number
}) {
  const { ref, isInView } = useInView()
  const isReversed = index % 2 === 1
  const Icon = feature.icon

  // Highlight the accent word in the title
  const titleParts = feature.title.split(feature.accentWord)

  return (
    <ParallaxSection>
      <div
        ref={ref}
        className={`grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-20 items-center ${
          isReversed ? 'lg:[direction:rtl]' : ''
        }`}
      >
        {/* ── Text Content ── */}
        <div
          className={`[direction:ltr] ${isReversed ? 'anim-fade-in-right' : 'anim-fade-in-left'} ${isInView ? 'in-view' : ''}`}
        >
          {/* Subtitle tag */}
          <div
            className={`flex items-center gap-3 mb-5 anim-fade-in-up anim-delay-1 ${isInView ? 'in-view' : ''}`}
          >
            <div className="w-11 h-11 rounded-xl bg-red-600/10 border border-red-600/20 flex items-center justify-center">
              <Icon size={22} className="text-red-500" />
            </div>
            <span className="text-sm font-semibold text-red-500 uppercase tracking-widest">
              {feature.subtitle}
            </span>
          </div>

          {/* Title with gradient accent */}
          <h2 className="text-3xl sm:text-4xl lg:text-[2.75rem] font-extrabold text-white leading-[1.15] tracking-[-0.03em] mb-5">
            {titleParts[0]}
            <span className="gradient-text-red">{feature.accentWord}</span>
            {titleParts[1] || ''}
          </h2>

          <p className="text-base sm:text-lg text-zinc-400 mb-8 leading-relaxed max-w-lg">
            {feature.description}
          </p>

          {/* Bullet List */}
          <ul className="space-y-4">
            {feature.bullets.map((bullet, j) => {
              const BulletIcon = bullet.icon
              return (
                <li
                  key={bullet.text}
                  className={`flex items-start gap-3 group anim-fade-in-left anim-delay-${Math.min(j + 2, 8)} ${isInView ? 'in-view' : ''}`}
                >
                  <div className="w-9 h-9 rounded-lg bg-white/[0.04] border border-white/[0.06] flex items-center justify-center flex-shrink-0 mt-0.5 group-hover:bg-red-600/10 group-hover:border-red-600/20 transition-colors duration-300">
                    <BulletIcon size={15} className="text-red-500/80 group-hover:text-red-400 transition-colors" />
                  </div>
                  <span className="text-sm text-zinc-300 leading-relaxed pt-1.5">
                    {bullet.text}
                  </span>
                </li>
              )
            })}
          </ul>
        </div>

        {/* ── Visual Card ── */}
        <div
          className={`[direction:ltr] ${isReversed ? 'anim-fade-in-left' : 'anim-fade-in-right'} anim-delay-2 ${isInView ? 'in-view' : ''}`}
        >
          <ScreenshotCard feature={feature} isInView={isInView} />
        </div>
      </div>

      {/* Section divider — subtle gradient line */}
      <div className="mt-16 lg:mt-24 mx-auto max-w-xs h-px bg-gradient-to-r from-transparent via-white/[0.06] to-transparent" />
    </ParallaxSection>
  )
}

/* ── Main Page ──────────────────────────────────────────── */
export default function FeaturesPage() {
  const { ref: ctaRef, isInView: ctaInView } = useInView()

  return (
    <div className="min-h-screen">
      {/* ── Hero ── */}
      <section className="section relative overflow-hidden">
        {/* Background glow */}
        <div className="absolute inset-0 gradient-mesh-hero pointer-events-none" />

        <div className="section-inner text-center relative">
          <div
            className="anim-fade-in-up in-view inline-flex items-center gap-2 px-4 py-1.5 rounded-full border border-white/[0.06] bg-white/[0.03] backdrop-blur-sm mb-8"
          >
            <Sparkles size={14} className="text-red-500" />
            <span className="text-sm text-zinc-400 font-medium">All-in-one platform</span>
          </div>

          <h1
            className="anim-fade-in-up anim-delay-1 in-view text-3xl sm:text-4xl lg:text-5xl xl:text-6xl font-extrabold leading-[1.1] tracking-[-0.03em] mb-6"
          >
            Everything You Need to{' '}
            <span className="gradient-text-red">Run Your Business</span>
          </h1>

          <p
            className="anim-fade-in-up anim-delay-2 in-view text-lg sm:text-xl text-zinc-400 max-w-2xl mx-auto leading-relaxed"
          >
            From first contact to final invoice, BuilderLync handles every step.
            Explore the features that make contractors switch.
          </p>

          {/* Scroll hint */}
          <div
            className="anim-fade-in anim-delay-8 in-view mt-12 flex flex-col items-center gap-2"
          >
            <span className="text-xs text-zinc-600 uppercase tracking-widest">Scroll to explore</span>
            <div
              className="w-5 h-8 rounded-full border border-white/[0.08] flex items-start justify-center pt-1.5"
              style={{ animation: 'scroll-hint-bounce 1.5s ease-in-out infinite' }}
            >
              <div className="w-1 h-2 rounded-full bg-red-500/60" />
            </div>
          </div>
        </div>
      </section>

      {/* ── Feature Sections ── */}
      {features.map((feature, i) => (
        <FeatureBlock key={feature.title} feature={feature} index={i} />
      ))}

      <RelatedGuides
        badge="Explore"
        cta="Learn more"
        heading="Go Deeper on Each Capability"
        subheading="Dedicated pages for the workflows contractors ask about most."
        guides={FEATURES_LINKS}
      />

      {/* ── Bottom CTA ── */}
      <section className="section relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-t from-red-600/[0.03] via-transparent to-transparent pointer-events-none" />

        <div ref={ctaRef} className="section-inner">
          <div
            className={`anim-fade-in-up ${ctaInView ? 'in-view' : ''} glass-card-premium p-8 sm:p-12 lg:p-16 text-center max-w-3xl mx-auto`}
          >
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-red-600/10 border border-red-600/20 mb-6">
              <Zap size={14} className="text-red-500" />
              <span className="text-xs font-semibold text-red-400 uppercase tracking-wider">
                Get Started
              </span>
            </div>

            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-extrabold mb-4 tracking-[-0.02em]">
              Ready to See It{' '}
              <span className="gradient-text-red">In Action</span>?
            </h2>

            <p className="text-zinc-400 mb-8 max-w-lg mx-auto leading-relaxed">
              Book a personalized demo and see how BuilderLync transforms your
              roofing business.
            </p>

            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <AttributedCtaLink
                href={BOOK_A_DEMO_URL}
                page="features"
                cta="Bottom CTA — Book a Demo"
                target="_blank"
                rel="noopener noreferrer"
                className="btn-primary text-base px-8 py-4 glow-red-sm"
              >
                Book a Demo
                <ArrowRight size={18} />
              </AttributedCtaLink>
              <Link href="/pricing" className="btn-ghost text-base px-8 py-4">
                View Pricing
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* CSS for scroll hint bounce animation */}
      <style jsx>{`
        @keyframes scroll-hint-bounce {
          0%, 100% { transform: translateY(0); }
          50% { transform: translateY(8px); }
        }
      `}</style>
    </div>
  )
}
